import type { ColumnDef } from '@/shared/types/table-filters.types';
import type { Company, CompanySize, CompanyStatus } from '../types/company.types';
import { sizeLabels, statusConfig } from '../utils/company-row.utils';

const COMPANY_SIZES: CompanySize[] = ['SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'];

/** Sorting by size means headcount, not the alphabet: ENTERPRISE is the biggest, not the first. */
const COMPANY_SIZE_RANK: Record<CompanySize, number> = Object.fromEntries(
  COMPANY_SIZES.map((size, index) => [size, index])
) as Record<CompanySize, number>;

const COMPANY_STATUSES: CompanyStatus[] = ['ACTIVE', 'SUSPENDED', 'DELETED'];

// Reuses the badge labels so the filter and the table never drift apart.
const COMPANY_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  COMPANY_STATUSES.map((status) => [status, statusConfig[status].label])
);

/** Same "City, Country" string the table cell shows; undefined when neither is known. */
export function companyLocation(company: Company): string | undefined {
  return [company.city, company.country].filter(Boolean).join(', ') || undefined;
}

/** Filterable/sortable company fields, shared by the desktop table and the mobile cards. */
export const companyTableColumns: ColumnDef<Company>[] = [
  {
    key: 'name',
    label: 'Name',
    filterType: 'text',
    accessor: (company) => company.name,
    sortable: true,
  },
  {
    key: 'email',
    label: 'Email',
    filterType: 'text',
    accessor: (company) => company.email,
    sortable: true,
  },
  {
    key: 'industry',
    label: 'Industry',
    filterType: 'text',
    accessor: (company) => company.industry,
    sortable: true,
  },
  {
    key: 'location',
    label: 'Location',
    filterType: 'text',
    accessor: companyLocation,
    sortable: true,
  },
  {
    key: 'companySize',
    label: 'Size',
    filterType: 'select',
    accessor: (company) => company.companySize,
    options: COMPANY_SIZES,
    optionLabels: sizeLabels,
    sortValue: (company) =>
      company.companySize ? COMPANY_SIZE_RANK[company.companySize] : undefined,
    sortable: true,
  },
  {
    key: 'status',
    label: 'Status',
    filterType: 'select',
    accessor: (company) => company.status,
    options: COMPANY_STATUSES,
    optionLabels: COMPANY_STATUS_LABELS,
    sortable: true,
  },
  {
    key: 'createdAt',
    label: 'Created',
    filterType: 'date-range',
    accessor: (company) => company.createdAt,
    sortable: true,
  },
];
